import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const WeightLoss = () => {
  // State variables for weight loss calculation
  const [currentWeight, setCurrentWeight] = useState(0); // User's current weight
  const [targetWeight, setTargetWeight] = useState(0); // User's target weight
  const [caloricDeficit, setCaloricDeficit] = useState(500); // Daily caloric deficit
  const [weightLossPerWeek, setWeightLossPerWeek] = useState(0); // Weight loss per week
  const [estimatedWeeks, setEstimatedWeeks] = useState(0); // Estimated time to reach goal
  const [weightUnit, setWeightUnit] = useState("lbs"); // Unit for weight (lbs, kg, stone, pounds)
  const [deficitUnit, setDeficitUnit] = useState("kcal"); // Unit for caloric deficit (kcal, calories, joules, kilojoules)

  // Event handlers to update state values
  const handleCurrentWeightChange = (e) =>
    setCurrentWeight(parseFloat(e.target.value) || 0);
  const handleTargetWeightChange = (e) =>
    setTargetWeight(parseFloat(e.target.value) || 0);
  const handleCaloricDeficitChange = (e) =>
    setCaloricDeficit(parseFloat(e.target.value) || 0);
  const handleWeightUnitChange = (e) => setWeightUnit(e.target.value);
  const handleDeficitUnitChange = (e) => setDeficitUnit(e.target.value);

  // useEffect hook to calculate weight loss and timeline
  useEffect(() => {
    const calorieToWeightConversion = 3500; // Calories per pound of weight
    let weightConversionFactor = 1; // Conversion factor for weight unit
    let caloricDeficitInKcal = caloricDeficit; // Default caloric deficit in kcal

    // Convert current weight and target weight based on the selected unit
    if (weightUnit === "kg") {
      weightConversionFactor = 2.20462; // 1 kg = 2.20462 lbs
    } else if (weightUnit === "stone") {
      weightConversionFactor = 14; // 1 stone = 14 lbs
    }

    // Convert caloric deficit if the unit is not kcal
    if (deficitUnit === "calories") {
      caloricDeficitInKcal = caloricDeficit * 1; // 1 cal = 1 calorie
    } else if (deficitUnit === "joules") {
      caloricDeficitInKcal = caloricDeficit / 4.184; // 1 joule = 0.239 calories
    } else if (deficitUnit === "kilojoules") {
      caloricDeficitInKcal = caloricDeficit / 4184; // 1 kJ = 0.239 kcal
    }

    if (currentWeight > targetWeight && caloricDeficitInKcal > 0) {
      const totalWeightLossNeeded =
        (currentWeight - targetWeight) * weightConversionFactor; // Total weight to lose in lbs
      const totalCaloricDeficit = caloricDeficitInKcal * 7; // Weekly caloric deficit
      const weeklyWeightLoss = totalCaloricDeficit / calorieToWeightConversion; // Weight loss per week
      setWeightLossPerWeek(weeklyWeightLoss);
      setEstimatedWeeks(totalWeightLossNeeded / weeklyWeightLoss); // Time required in weeks
    } else {
      setWeightLossPerWeek(0);
      setEstimatedWeeks(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [currentWeight, targetWeight, caloricDeficit, weightUnit, deficitUnit]);

  return (
    <Layout title="Weight Loss Calculator" path="weight-loss-calculator">
      <section className="section-padding" id="weight-loss-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Weight Loss Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Weight Loss Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Current Weight Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="currentWeight" className="form-label">
                        Current Weight
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Weight:</strong> The weight of the user at the start of the weight loss process. Please enter your current weight in the unit you've selected (lbs, kg, or stone)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="currentWeight"
                        placeholder="Enter current weight"
                        value={currentWeight || ""}
                        onChange={handleCurrentWeightChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={weightUnit}
                        onChange={handleWeightUnitChange}
                      >
                        <option value="lbs">lbs</option>
                        <option value="kg">kg</option>
                        <option value="stone">stone</option>
                      </select>
                    </div>

                    {/* Target Weight Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="targetWeight" className="form-label">
                        Target Weight
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Target Weight:</strong> The weight the user aims to achieve. Please enter the desired weight in the unit you've selected (lbs, kg, or stone). This weight will serve as the goal to calculate the estimated weeks to reach it."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="targetWeight"
                        placeholder="Enter target weight"
                        value={targetWeight || ""}
                        onChange={handleTargetWeightChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={weightUnit}
                        onChange={handleWeightUnitChange}
                      >
                        <option value="lbs">lbs</option>
                        <option value="kg">kg</option>
                        <option value="stone">stone</option>
                      </select>
                    </div>

                    {/* Caloric Deficit Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="caloricDeficit" className="form-label">
                        Daily Caloric Deficit
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Caloric Deficit:</strong> The number of calories you plan to burn daily through exercise or diet. Choose the unit of measurement for the caloric deficit."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="caloricDeficit"
                        placeholder="Enter daily caloric deficit"
                        value={caloricDeficit || ""}
                        onChange={handleCaloricDeficitChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={deficitUnit}
                        onChange={handleDeficitUnitChange}
                      >
                        <option value="kcal">kcal</option>
                        <option value="calories">calories</option>
                        <option value="joules">joules</option>
                        <option value="kilojoules">kilojoules</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Weekly Weight Loss:{" "}
                        <span className="fw-bold ms-3">
                          {weightLossPerWeek === 0
                            ? "0.00"
                            : weightLossPerWeek.toFixed(2)}{" "}
                          {weightUnit === "lbs"
                            ? "lbs"
                            : weightUnit === "kg"
                            ? "kg"
                            : "stone"}
                        </span>
                      </h6>
                      <h6>
                        Estimated Weeks to Reach Goal:{" "}
                        <span className="fw-bold ms-3">
                          {estimatedWeeks === 0
                            ? "0.00"
                            : estimatedWeeks.toFixed(2)}{" "}
                          weeks
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Weight Loss Calculator
                </h3>

                {/* What is a Weight Loss Calculator? */}
                <h4 className="mt-3 mb-3">What is a Weight Loss Calculator?</h4>
                <p>
                  The **Weight Loss Calculator** helps estimate the timeline
                  required to achieve a weight-loss goal. It considers factors
                  such as current and target weight, daily caloric deficit, and
                  the relationship between calories and weight.
                </p>

                {/* Why Use a Weight Loss Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Weight Loss Calculator?</h4>
                <p>
                  Using this calculator aids in setting realistic weight-loss
                  goals, planning healthier lifestyle changes, and monitoring
                  progress over time.
                </p>

                {/* Benefits of Using a Weight Loss Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Weight Loss Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Supports Health and Fitness Planning:</strong>{" "}
                    Provides a structured approach to weight management.
                  </li>
                  <li>
                    <strong>Sets Realistic Weight-Loss Goals:</strong> Ensures
                    goals are achievable based on caloric intake and activity
                    levels.
                  </li>
                  <li>
                    <strong>Encourages Healthier Lifestyle Changes:</strong>{" "}
                    Motivates users to adopt sustainable habits.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Weight Loss Calculator?
                </h4>
                <p>This calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Individuals:</strong> Helps track personal fitness
                    goals.
                  </li>
                  <li>
                    <strong>Fitness Trainers:</strong> Assists in designing
                    effective weight-loss programs for clients.
                  </li>
                  <li>
                    <strong>Dieticians:</strong> Supports dietary planning and
                    caloric management.
                  </li>
                </ul>

                {/* Additional Tips */}
                <h4 className="mt-4 mb-3">Tips for Successful Weight Loss</h4>
                <p>
                  Combine a healthy diet, regular exercise, and consistent
                  monitoring for effective results. Consult a healthcare
                  professional for personalized advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WeightLoss;
